import dayjs from 'dayjs';
import * as React from 'react';
import { useIntl } from 'react-intl';
import useVirtual, { LoadMoreEvent } from 'react-cool-virtual';
import EventInTable from './EventInTable';
import { IInfoRecord } from '../interfaces';
import {
  ALARM_ID,
  END_TYPE,
  EVENT_ID,
  MIDNIGHT_ID,
  PARKING_TYPE,
  START_TYPE,
  WAYPOINT_ID,
} from '../constants';
import { getDay, loadAnotherEvents } from '../TripsOverviewService';

interface IInfoTableModalProps {
  firstData: IInfoRecord[];
  count: number;
  currentPage: number;
  start: string;
  rideId: string;
  customerId: number;
  filters: string[];
  information: string;
  isAnonym: boolean;
}

function InfoTable(props: IInfoTableModalProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [records, setRecords] = React.useState<IInfoRecord[]>(props.firstData);
  const endEventIndex = records.findIndex((i) => i.type === END_TYPE);
  const [isFirstLaodFromRideRecord, setIsFirstLaodFromRideRecord] = React.useState(true);
  const [loader, setLoader] = React.useState(false);
  const [actualSizeOfRecords, setActualSizeOfRecords] = React.useState<number>(0);
  const isItemLoadedArr: boolean[] = React.useMemo(() => [], []);
  isItemLoadedArr[props.count] = true;

  React.useEffect(() => {
    setCurrentPage(props.currentPage);
  }, [props.currentPage]);

  const getIconColor = (type: string) => {
    let color = '';
    switch (type) {
      case START_TYPE:
        color = '#b7b7b7';
        break;
      case PARKING_TYPE:
        color = '#3d72c8';
        break;
      case END_TYPE:
        color = '#b7b7b7';
        break;
      case WAYPOINT_ID:
        color = '#05b590';
        break;
      case ALARM_ID:
        color = '#b50536';
        break;
      case EVENT_ID:
        color = '#3d278f';
        break;
      default:
        color = '#0d336f';
    }
    return color;
  };

  const handleAnotherEvents = React.useCallback(
    async (event: LoadMoreEvent) => {
      if (!loader) {
        if (!isFirstLaodFromRideRecord) {
          const { loadIndex } = event;

          isItemLoadedArr[loadIndex] = true;
          if (records?.length === props.count) {
            isItemLoadedArr[loadIndex] = false;
            return;
          }

          setLoader(true);
          const result = await loadAnotherEvents(
            props.rideId,
            String(props.customerId),
            currentPage,
            props.information,
            props.filters
          );
          setLoader(false);

          if (result && records.length > 0) {
            setCurrentPage((oldPage) => oldPage + 1);
            setActualSizeOfRecords(result.length - 1);
            const newRecords = records.concat(result);
            setRecords(newRecords);
          }
        } else {
          setIsFirstLaodFromRideRecord(false);
        }
      }
    },
    [
      isFirstLaodFromRideRecord,
      currentPage,
      loader,
      isItemLoadedArr,
      records,
      props.count,
      props.rideId,
      props.customerId,
      props.information,
      props.filters,
    ]
  );

  const { outerRef, innerRef, items } = useVirtual<HTMLUListElement | HTMLLIElement>({
    itemCount: records.length,
    loadMoreCount: actualSizeOfRecords,
    isItemLoaded: (loadIndex) => isItemLoadedArr[loadIndex],
    loadMore: (event) => handleAnotherEvents(event),
  });

  const getTimeClassName = (type: string, isAnonym: boolean) => {
    if (type === START_TYPE) {
      return 'font-weight-bold text-green-dark';
    }
    if (type === END_TYPE && !isAnonym) {
      return 'font-weight-bold text-primary';
    }
    if (type === PARKING_TYPE) {
      return 'font-weight-bold text-blue';
    }
    if (type === MIDNIGHT_ID) {
      return 'font-weight-bold text-blue-dark';
    }
    return '';
  };

  const getIconClassName = (type: string) => {
    if (type === START_TYPE) {
      return 'key';
    }
    if (type === END_TYPE) {
      return 'parking';
    }
    if (type === PARKING_TYPE) {
      return 'pause';
    }
    if (type === MIDNIGHT_ID) {
      return 'midnight';
    }
    return type;
  };

  const loading = (): JSX.Element => (
    <tbody>
      <tr>
        <td
          style={{
            display: 'flex',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            width: '100%',
            background: '#fff',
          }}
        >
          <h6
            className=" text-black"
            style={{
              fontWeight: 'bold',
            }}
          >
            {f({ id: 'waypoints.loading' })}
          </h6>
        </td>
      </tr>
    </tbody>
  );

  const handleRecordType = (record: IInfoRecord): JSX.Element => {
    if (record.type === MIDNIGHT_ID) {
      return <span>0:00</span>;
    }

    if (record.activatedAt) {
      return <span>{dayjs(record.activatedAt).format('HH:mm:ss')}</span>;
    }
    return (
      <div className="text-center">
        <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />
      </div>
    );
  };

  const getDatacyIndex = (index: number, type: string) => {
    if (type === START_TYPE) {
      return 'start';
    }
    if (type === END_TYPE) {
      return 'end';
    }
    if (endEventIndex < index) {
      return index - 1;
    }
    return index;
  };

  return (
    <div className="b-table-list-modal-content" ref={outerRef as React.LegacyRef<HTMLTableElement>}>
      <div className="text-gray-light fs-1 font-weight-bold ml-2 mb-2">
        {/* {dayjs(props.start).format('DD.MM.YYYY')} -{' '}
        {f({ id: `date.${getDay(props.start)}` })} */}
        {dayjs(props.start).format('DD.MM.YYYY')} - {f({ id: `date.${getDay(props.start)}` })}
      </div>

      <table className="table table-striped table-bordered m-0 table-export-knihy-jazd">
        <tbody
          ref={innerRef as React.LegacyRef<HTMLTableSectionElement>}
          style={{ display: 'block' }}
        >
          {items.map(({ index, measureRef }) => {
            const showLoading = index === records.length - 1 && records.length < props.count;

            return (
              <React.Fragment key={`records-modal-${index}`}>
                {records[index] && (
                  <tr
                    ref={measureRef}
                    data-cy={`ride_info-detail-${getDatacyIndex(index, records[index].type)}`}
                  >
                    <td
                      className={`text-nowrap ${getTimeClassName(
                        records[index].type,
                        !records[index].activatedAt
                      )}`}
                    >
                      {handleRecordType(records[index])}
                    </td>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <td className="text-center td-w-40">
                      <i
                        style={{
                          color: getIconColor(records[index].type),
                        }}
                        className={`cmd-icons cmd-icon-${getIconClassName(
                          records[index].type
                        )} fs-4`}
                      />
                    </td>

                    <EventInTable
                      index={index}
                      text={
                        props.isAnonym &&
                        (records[index].type === START_TYPE || records[index].type === END_TYPE)
                          ? f({ id: 'tables.tripsOverview.column.datetime.anonymized' })
                          : records[index].title
                      }
                      type={records[index].type}
                      activatedAt={records[index].activatedAt}
                      duration={records[index].duration}
                    />
                  </tr>
                )}
                {showLoading && loader && loading()}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default React.memo(InfoTable);
